import { useEffect, useState } from 'react';
import SchedulePicker, { buildScheduleDateOptions } from '@/components/booking/SchedulePicker';

interface Props {
  onSlotSelect?: (date: string, time: string) => Promise<void> | void;
  hasPurchased?: boolean;
  onGoToPurchase?: () => void;
  defaultDate?: string;
  defaultTime?: string;
}

const BookingCalendar = ({ onSlotSelect, hasPurchased = false, onGoToPurchase, defaultDate = '', defaultTime = '' }: Props) => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const dateOptions = buildScheduleDateOptions(21);

  useEffect(() => {
    if (!hasPurchased) {
      return;
    }
    setSelectedDate(defaultDate || '');
    setSelectedSlot(defaultTime || '');
    setConfirmed(false);
    setSaveError('');
  }, [hasPurchased, defaultDate, defaultTime]);

  const handleConfirm = () => {
    if (!selectedDate || !selectedSlot || saving) {
      setSaveError('请先选择完整的预约日期和时间');
      return;
    }
    setSaveError('');
    setSaving(true);
    Promise.resolve(onSlotSelect?.(selectedDate, selectedSlot))
      .then(() => {
        setConfirmed(true);
      })
      .catch((error: unknown) => {
        setSaveError(error instanceof Error ? error.message : '预约时间更新失败，请稍后再试');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (!hasPurchased) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-bold text-navy-800 text-base flex items-center gap-2">
            <i className="ri-calendar-line text-violet-500" />
            選擇諮詢時間
          </h3>
          <p className="text-slate-400 text-xs mt-0.5">所有時間均為香港時間 (HKT · UTC+8)</p>
        </div>
        <div className="relative p-5">
          <div className="blur-sm pointer-events-none select-none opacity-50">
            <SchedulePicker
              selectedDate=""
              selectedTime=""
              onDateChange={() => {}}
              onTimeChange={() => {}}
              dateOptions={dateOptions}
            />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white/80 backdrop-blur-[2px]">
            <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-full">
              <i className="ri-lock-line text-2xl text-slate-400" />
            </div>
            <div className="text-center px-6">
              <p className="font-semibold text-navy-800 text-sm mb-1">請先完成預約付款</p>
              <p className="text-slate-400 text-xs leading-relaxed">付款成功後即可解鎖行事曆，選擇你的諮詢時間</p>
            </div>
            <button
              onClick={onGoToPurchase}
              className="px-5 py-2.5 bg-violet-600 text-white text-sm font-semibold rounded-lg cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap"
            >
              立即預約諮詢
              <i className="ri-arrow-right-line ml-2" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (confirmed && selectedDate && selectedSlot) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-violet-100 rounded-full mx-auto mb-4">
          <i className="ri-calendar-check-line text-2xl text-violet-600" />
        </div>
        <h3 className="font-bold text-navy-800 text-lg mb-2">預約申請已提交！</h3>
        <p className="text-slate-500 text-sm mb-1">
          {selectedDate} · {selectedSlot} (HKT)
        </p>
        <p className="text-slate-400 text-xs mb-6">導師將在 24 小時內確認，並傳送視訊會議連結至你的信箱</p>
        <button
          onClick={() => {
            setConfirmed(false);
            setSelectedDate('');
            setSelectedSlot('');
          }}
          className="px-5 py-2 border border-violet-200 text-violet-600 text-sm font-semibold rounded-lg cursor-pointer hover:bg-violet-50 transition-colors whitespace-nowrap"
        >
          重新選擇時間
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100">
        <h3 className="font-bold text-navy-800 text-base flex items-center gap-2">
          <i className="ri-calendar-line text-violet-500" />
          選擇諮詢時間
        </h3>
        <p className="text-slate-400 text-xs mt-0.5">所有時間均為香港時間 (HKT · UTC+8)</p>
      </div>

      <div className="p-5">
        {defaultDate && defaultTime ? (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            已为你载入最近预约时间：{defaultDate} {defaultTime}（可修改）
          </div>
        ) : null}
        <SchedulePicker
          selectedDate={selectedDate}
          selectedTime={selectedSlot}
          onDateChange={setSelectedDate}
          onTimeChange={setSelectedSlot}
          dateOptions={dateOptions}
        />

        <button
          onClick={handleConfirm}
          disabled={saving}
          className="mt-4 w-full py-3 bg-violet-600 text-white text-sm font-semibold rounded-lg cursor-pointer hover:bg-violet-700 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? '提交中...' : `確認預約 ${selectedDate || ''} ${selectedSlot || ''}`}
          <i className="ri-arrow-right-line ml-2" />
        </button>
        {saveError ? <p className="mt-2 text-xs text-rose-500">{saveError}</p> : null}
      </div>
    </div>
  );
};

export default BookingCalendar;
